import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { useAppSelector } from 'store/hooks';
import { selectUserIdByFeed } from 'features/streaming/streamingSlice';
import { selectLocalUserId, selectUserById } from 'features/users/usersSlice';
import { RootState } from 'store/store';
import { TrackScore } from 'utils/webrtc/stats/types';
import { bucketColor, mosBucket } from 'features/system-stats/mosBucket';
import {
  pickSummaryTrack,
  selectTrackScoresByFeed,
  UNRESOLVED,
} from 'features/system-stats/trackScoresByFeed';
import Icon from 'components/Icon';

const formatMOS = (value: number | null): string => (value === null ? '—' : value.toFixed(2));

const mosColor = (theme: Theme, score: number | null): string =>
  bucketColor(theme, mosBucket(score));

const buildNotes = (score: TrackScore): string => {
  const parts: string[] = [];
  if (score.score === null && score.reason) {
    parts.push(score.reason);
  }
  if (score.limitingFactor) {
    parts.push(`limited by ${score.limitingFactor}`);
  }
  if (score.audioImpairmentPct !== undefined && score.audioImpairmentPct > 0) {
    parts.push(`${score.audioImpairmentPct.toFixed(1)}% impairment`);
  }
  return parts.join(' · ');
};

const trackLabel = (s: TrackScore): string =>
  `${s.kind} ${s.direction}${s.isScreenshare ? ' · SS' : ''}${s.rid ? ` · ${s.rid}` : ''}`;

const directionRank: Record<TrackScore['direction'], number> = {
  sending: 0,
  receiving: 1,
};
const kindRank: Record<TrackScore['kind'], number> = { audio: 0, video: 1 };
const ridRank = (rid?: string) => (rid === undefined ? -1 : rid.charCodeAt(0));

const sortTracks = (tracks: TrackScore[]): TrackScore[] =>
  [...tracks].sort(
    (a, b) =>
      directionRank[a.direction] - directionRank[b.direction] ||
      kindRank[a.kind] - kindRank[b.kind] ||
      ridRank(a.rid) - ridRank(b.rid)
  );

const ParticipantAccordion = ({ feedKey, tracks }: { feedKey: string; tracks: TrackScore[] }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const userId = useAppSelector((state: RootState) =>
    feedKey !== UNRESOLVED ? selectUserIdByFeed(state, feedKey) : undefined
  );
  const user = useAppSelector((state: RootState) =>
    userId ? selectUserById(state, userId) : undefined
  );
  const isLocal = useAppSelector(
    (state: RootState) => userId !== undefined && userId === selectLocalUserId(state)
  );
  const name = user?.name ?? '—';
  const sorted = sortTracks(tracks);
  const summary = pickSummaryTrack(tracks);
  const summaryScore = summary?.score ?? null;
  const summaryNotes = summary ? buildNotes(summary) : '';
  const tracksLabel = `${tracks.length} ${tracks.length === 1 ? 'track' : 'tracks'}`;

  return (
    <Accordion
      disableGutters
      elevation={0}
      square
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<Icon name="dropdown-arrow" sx={{ fontSize: 16 }} />}
        sx={{ px: 1 }}
      >
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            columnGap: 2,
            rowGap: 0.5,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
            <Typography sx={{ fontWeight: 700 }}>
              {name}
              {isLocal && (
                <Typography component="span" sx={{ ml: 1, color: 'grey.400', fontWeight: 400 }}>
                  ({t('common:you')})
                </Typography>
              )}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {tracksLabel}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right', minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                color: mosColor(theme, summaryScore),
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatMOS(summaryScore)}
            </Typography>
            {summaryNotes && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {summaryNotes}
              </Typography>
            )}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1, pt: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {sorted.map((s, i) => {
            const notes = buildNotes(s);
            return (
              <Box
                key={`${s.mid}-${s.rid ?? 'd'}-${s.direction}-${s.kind}-${i}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  columnGap: 2,
                  py: 0.75,
                  pl: 1.5,
                  borderLeft: 2,
                  borderColor: mosColor(theme, s.score),
                }}
              >
                <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
                  <Typography variant="body2">{trackLabel(s)}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    mid {s.mid}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right', minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: mosColor(theme, s.score),
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatMOS(s.score)}
                  </Typography>
                  {notes && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {notes}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const PerTrackScoresTable = memo(() => {
  const groups = useAppSelector(selectTrackScoresByFeed);
  if (groups.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography component="h3" variant="subtitle1" gutterBottom>
        Per-track scores
      </Typography>
      <Box>
        {groups.map(([feedKey, tracks]) => (
          <ParticipantAccordion key={feedKey} feedKey={feedKey} tracks={tracks} />
        ))}
      </Box>
    </Box>
  );
});

export default PerTrackScoresTable;
